// ✅ Updated full version with tolerant param extraction & modern certificate UI
import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import QRCode from "react-qr-code";

function extractEntryIdFromPath(pathname: string) {
  if (!pathname) return null;
  const parts = pathname.split("/").filter(Boolean);
  const last = parts[parts.length - 1] || null;
  if (!last) return null;
  if (/^[0-9a-fA-F]{24}$/.test(last) || last.length >= 6) return last;
  return null;
}

export default function LotteryCertificatePage() {
  const params = useParams<{ entryId?: string }>();
  const location = useLocation();

  const q = new URLSearchParams(location.search);
  const entryIdParam =
    params?.entryId ||
    q.get("entryId") ||
    q.get("id") ||
    extractEntryIdFromPath(window.location.pathname);

  console.debug("[Certificate] window.location.pathname:", window.location.pathname);
  console.debug("[Certificate] useParams():", params);
  console.debug("[Certificate] entryIdParam resolved:", entryIdParam);

  const [entry, setEntry] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entryIdParam) {
      setError("No entry id provided in URL");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const token =
          localStorage.getItem("consumerToken") ||
          localStorage.getItem("authToken") ||
          localStorage.getItem("jeptags_token") ||
          localStorage.getItem("token");

        const API_BASE =
          process.env.REACT_APP_API_BASE_URL ||
          //import.meta.env.VITE_API_URL ||
          "";
        const url = `${API_BASE}/consumer-lottery/entry/${entryIdParam}`;

        console.debug("[Certificate] fetching URL:", url, "with token:", !!token);

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });

        const data = await res.json().catch(() => null);
        if (!res.ok || !data || !data.success) {
          throw new Error(data?.message || `Request failed: ${res.status}`);
        }

        setEntry({ ...data.entry, entryId: data.entry._id || entryIdParam });
      } catch (e: any) {
        console.error("[Certificate] fetch error:", e);
        setError(e.message || "Failed to load entry");
      } finally {
        setLoading(false);
      }
    })();
  }, [entryIdParam]);

  if (loading)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin h-10 w-10 border-b-2 border-purple-600 rounded-full"></div>
        <p className="text-gray-600 mt-3">Loading Lottery Certificate...</p>
      </div>
    );

  if (error || !entry)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <h2 className="text-xl font-bold text-red-700 mb-2">Certificate Not Available</h2>
        <p className="text-gray-600 mb-4">{error || "Entry not found"}</p>
        <Link
          to="/consumer/my-entries"
          className="px-4 py-2 bg-purple-600 text-white rounded shadow hover:bg-purple-700"
        >
          Back to My Entries
        </Link>
      </div>
    );

  const isWinner = entry.status === "winner" || entry.status === "claimed";
  const certificateId = `CERT-${entry.tagId}-${entry.entryId}`;
  const issueDate = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const verifyURL = `${window.location.origin}/certificate/${entry.entryId}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-100 to-white py-10">
      {/* Header */}
      <div className="max-w-4xl mx-auto bg-gradient-to-r from-pink-500 to-purple-500 text-white text-center rounded-t-2xl shadow-lg p-6">
        <h1 className="text-3xl font-bold">🎟️ JepTags Lottery Certificate</h1>
        <p className="text-sm opacity-90">Official Certificate of Lottery Participation</p>
        <div className="mt-3 text-sm bg-white/25 px-3 py-1 inline-block rounded-full">
          Certificate ID: {certificateId}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto bg-white rounded-b-2xl shadow-lg p-8">
        {isWinner && (
          <div className="text-center border border-green-300 bg-green-50 rounded-xl py-8 mb-10">
            <div className="text-3xl font-bold text-green-700 mb-2">🎉 WINNER!</div>
            <div className="text-lg font-semibold text-gray-700">
              {entry.prize?.name || "Prize"}
            </div>
            <div className="text-4xl font-bold text-green-600 mt-2">
              ${entry.prize?.value || 0}
            </div>
          </div>
        )}

        {/* Information Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Entry Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-purple-700 font-semibold mb-3">ENTRY INFORMATION</h3>
            <p>
              <span className="font-medium">Entry ID:</span> {entry.entryId}
            </p>
            <p>
              <span className="font-medium">Entry Number:</span>{" "}
              <span className="text-purple-700 font-semibold">
                #{entry.entryId}
              </span>
            </p>
            <p>
              <span className="font-medium">Tag ID:</span> {entry.tagId}
            </p>
            <p>
              <span className="font-medium">Status:</span>{" "}
              <span
                className={`px-2 py-1 rounded-full text-xs ${
                  isWinner
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {entry.status.toUpperCase()}
              </span>
            </p>
          </div>

          {/* Participant Info */}
          <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
            <h3 className="text-pink-700 font-semibold mb-3">PARTICIPANT INFORMATION</h3>
            <p>
              <span className="font-medium">Email:</span>{" "}
              {entry.consumerEmail || "N/A"}
            </p>
            <p>
              <span className="font-medium">Joined At:</span>{" "}
              {entry.joinedAt ? new Date(entry.joinedAt).toLocaleString() : "N/A"}
            </p>
            <p>
              <span className="font-medium">Drawn At:</span>{" "}
              {entry.drawnAt ? new Date(entry.drawnAt).toLocaleString() : "N/A"}
            </p>
            {entry.claimedAt && (
              <p>
                <span className="font-medium">Claimed At:</span>{" "}
                <span className="text-green-700 font-semibold">
                  {new Date(entry.claimedAt).toLocaleString()}
                </span>
              </p>
            )}
          </div>

          {/* Lottery Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-blue-700 font-semibold mb-3">LOTTERY INFORMATION</h3>
            <p>
              <span className="font-medium">Lottery Name:</span>{" "}
              {entry.lottery?.name || "N/A"}
            </p>
            <p>
              <span className="font-medium">Draw Date:</span>{" "}
              {entry.lottery?.drawDate
                ? new Date(entry.lottery.drawDate).toLocaleDateString()
                : "N/A"}
            </p>
          </div>

          {/* QR Verification */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex flex-col items-center">
            <h3 className="text-indigo-700 font-semibold mb-3">VERIFICATION QR CODE</h3>
            <QRCode value={verifyURL} size={120} />
            <p className="text-xs text-gray-600 mt-2">
              Scan to verify certificate authenticity
            </p>
          </div>

          {/* GPS Verified */}
          {entry.gpsLocation && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 md:col-span-2">
              <h3 className="text-green-700 font-semibold mb-3">GPS VERIFIED</h3>
              <p>
                <span className="font-medium">Lat:</span>{" "}
                {entry.gpsLocation.latitude.toFixed(5)}
              </p>
              <p>
                <span className="font-medium">Long:</span>{" "}
                {entry.gpsLocation.longitude.toFixed(5)}
              </p>
              {entry.drawnAt && (
                <p>
                  <span className="font-medium">Verified:</span>{" "}
                  {new Date(entry.drawnAt).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-gray-600 text-sm mt-10 border-t pt-4">
          <p>
            This is an official JepTags lottery certificate issued on{" "}
            <span className="font-semibold">{issueDate}</span>
          </p>
          <p className="mt-1 text-xs">
            Certificate ID: {certificateId} | Entry ID: {entry.entryId}
          </p>
          <Link
            to="/consumer/my-entries"
            className="text-purple-600 hover:underline font-medium block mt-3"
          >
            ← Back to My Entries
          </Link>
        </div>
      </div>
    </div>
  );
}
